/** How the mail list shows people and times. */

import type { MailAddress, MessageSummary } from 'zitimail-shared-types';

export function displayName(a: MailAddress | undefined): string {
  return a ? a.name || a.address.split('@')[0] || a.address : '';
}

export function initials(a: MailAddress | undefined): string {
  const name = displayName(a).trim();
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  const letters =
    parts.length > 1
      ? `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`
      : name.slice(0, 2);
  return letters.toUpperCase() || '?';
}

/** A stable hue per address, so a sender always looks the same. */
export function hue(address: string): number {
  let h = 0;
  for (const c of address.toLowerCase()) {
    h = (h * 31 + c.charCodeAt(0)) % 360;
  }
  return h;
}

/** Time today, weekday this week, date otherwise; the year only when it differs. */
export function listDate(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  if (days === 0) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (days === 1) {
    return 'Yesterday';
  }
  if (days > 1 && days < 7) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  return d.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

export function fullDate(iso: string): string {
  return new Date(iso).toLocaleString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function size(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 ** 2) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

/** A subject with its Re:/Fwd: prefixes removed, for grouping. */
export function baseSubject(subject: string): string {
  return subject
    .replace(/^(\s*(re|fw|fwd|aw|wg|sv|antw)(\[\d+\])?\s*:\s*)+/i, '')
    .trim()
    .toLowerCase();
}

export interface Conversation {
  /** Newest first. */
  readonly messages: readonly MessageSummary[];
  readonly latest: MessageSummary;
  readonly unread: boolean;
  readonly flagged: boolean;
  readonly hasAttachments: boolean;
  /** Distinct senders, newest first. */
  readonly people: readonly MailAddress[];
}

/**
 * Groups a page of messages into conversations: a reply joins its parent by
 * Message-ID, and messages with the same base subject among the same
 * correspondents join each other. Grouping is over what is loaded, which is
 * what the list shows.
 */
export function conversations(messages: readonly MessageSummary[]): Conversation[] {
  const parent = new Map<number, number>();
  const find = (i: number): number => {
    let r = i;
    while (parent.has(r) && parent.get(r) !== r) {
      r = parent.get(r) as number;
    }
    return r;
  };
  const join = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) {
      parent.set(ra, rb);
    }
  };
  const byId = new Map<string, number>();
  const bySubject = new Map<string, { index: number; reply: boolean }>();
  messages.forEach((m, i) => {
    parent.set(i, i);
    if (m.messageId) {
      byId.set(m.messageId, i);
    }
  });
  messages.forEach((m, i) => {
    const replyTo = m.inReplyTo ? byId.get(m.inReplyTo) : undefined;
    if (replyTo !== undefined) {
      join(i, replyTo);
    }
    // Same subject joins only when a reply or forward is among them: two
    // unrelated messages both titled "Hello" are not a conversation.
    const subject = baseSubject(m.subject);
    const reply = /^\s*(re|fw|fwd|aw|wg|sv|antw)(\[\d+\])?\s*:/i.test(m.subject);
    if (subject) {
      const seen = bySubject.get(subject);
      if (seen && (reply || seen.reply)) {
        join(i, seen.index);
        seen.reply = true;
      } else if (!seen) {
        bySubject.set(subject, { index: i, reply });
      }
    }
  });
  const groups = new Map<number, MessageSummary[]>();
  messages.forEach((m, i) => {
    const root = find(i);
    groups.set(root, [...(groups.get(root) ?? []), m]);
  });
  return [...groups.values()]
    .map((list) => {
      const sorted = [...list].sort((a, b) => b.date.localeCompare(a.date));
      const latest = sorted[0] as MessageSummary;
      const people: MailAddress[] = [];
      for (const m of sorted) {
        if (!people.some((p) => p.address === m.from.address)) {
          people.push(m.from);
        }
      }
      return {
        messages: sorted,
        latest,
        unread: sorted.some((m) => !m.seen),
        flagged: sorted.some((m) => m.flagged),
        hasAttachments: sorted.some((m) => m.hasAttachments),
        people,
      };
    })
    .sort((a, b) => b.latest.date.localeCompare(a.latest.date));
}

const BLOCKS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'TR', 'PRE']);

/**
 * HTML as the text a plain-text writer sees: paragraphs become blank-line
 * separated, a quote becomes "> " lines, list items "- " lines, a link its
 * text and address.
 */
export function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent ?? '').replace(/\s+/g, ' ');
    }
    if (!(node instanceof Element)) {
      return '';
    }
    const inner = () => [...node.childNodes].map(walk).join('');
    switch (node.tagName) {
      case 'SCRIPT':
      case 'STYLE':
        return '';
      case 'BR':
        return '\n';
      case 'LI':
        return `- ${inner().trim()}\n`;
      case 'UL':
      case 'OL':
        return `${inner()}\n`;
      case 'BLOCKQUOTE': {
        const quoted = inner()
          .replace(/\n{3,}/g, '\n\n')
          .trim()
          .split('\n')
          .map((line) => (line ? `> ${line}` : '>'))
          .join('\n');
        return `${quoted}\n\n`;
      }
      case 'A': {
        const text = inner();
        const href = node.getAttribute('href') ?? '';
        return /^https?:/i.test(href) && href !== text.trim() ? `${text} <${href}>` : text;
      }
      default: {
        if (!BLOCKS.has(node.tagName)) {
          return inner();
        }
        // "-- " is the signature line mail apps look for; its space matters.
        const block = inner().trim();
        return `${block === '--' ? '-- ' : block}\n\n`;
      }
    }
  };
  return walk(doc.body)
    .split('\n')
    .map((line) => (line === '-- ' ? line : line.replace(/[ \t]+$/, '')))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Characters common in business mail that the two scripts write differently,
// as Simplified/Traditional pairs (each set holds only one side of a pair).
const PAIRS =
  '们們 这這 说說 为為 时時 会會 对對 发發 经經 后後 问問 过過 还還 没沒 样樣 么麼 与與 东東 车車 长長 门門 见見 学學 国國 个個 来來 现現 进進 动動 开開 关關 头頭 员員 题題 报報 价價 单單 让讓 请請 应應 该該 谢謝 听聽 书書 写寫 买買 卖賣 电電 话話 钱錢 银銀 业業 务務 专專 认認 识識 边邊 计計 划劃 设設 备備 产產 资資 讯訊 网網 络絡 联聯 纪紀 录錄 处處 体體 测測 试試 简簡 编編 码碼 邮郵 费費 确確 订訂 货貨 运運 总總 结結 际際 级級 线線 购購 汇匯 账帳 户戶 无無 实實 将將 从從 层層 区區 华華 湾灣 广廣 间間 闻聞 阅閱 规規 视視 览覽 觉覺 议議 记記 访訪 词詞 语語 读讀 课課 谈談 质質 贵貴 赛賽 转轉 软軟 达達 选選 递遞 钟鐘 错錯 闭閉 难難 页頁 项項 顺順 预預 领領 风風 飞飛 饭飯 马馬 鱼魚 黄黃 齐齊 龙龍 习習 乐樂 亲親 优優 传傳 众眾 双雙 变變 号號 图圖 场場 复復 寻尋 导導 岁歲 币幣 师師 帮幫 归歸 当當 战戰 护護 担擔 据據 换換 损損 数數 断斷 术術 机機 杂雜 权權 条條 标標 树樹 档檔 气氣 汉漢 济濟 满滿 灯燈 点點 热熱 爱愛 状狀 献獻 环環 疗療 盖蓋 础礎 积積 称稱 种種 签簽 类類 组組 细細 绩績 续續 维維 综綜 职職 胜勝 脑腦 节節 范範 药藥 获獲 营營 虽雖 补補 装裝 观觀 证證 评評 诉訴 询詢 详詳 误誤 调調 负負 责責 贴貼 赔賠 赵趙 跃躍 输輸 办辦 远遠 连連 适適 邻鄰 释釋 针針 链鏈 销銷 锁鎖 队隊 阶階 险險 随隨 隐隱 须須 顾顧 顿頓 频頻 额額 馆館 验驗';
const HANS_ONLY = new Set(PAIRS.split(' ').map((pair) => pair[0]));
const HANT_ONLY = new Set(PAIRS.split(' ').map((pair) => pair[1]));

/**
 * Which Chinese script `text` is in — Traditional (Taiwan, Hong Kong) or
 * Simplified (mainland, Singapore) — so it is set in that script's own face:
 * shared characters are drawn differently, and readers notice. Undefined
 * when there is no telling (no Chinese, or no characters that differ).
 */
export function chineseScript(text: string): 'zh-Hant' | 'zh-Hans' | undefined {
  let hans = 0;
  let hant = 0;
  for (const ch of text) {
    if (HANS_ONLY.has(ch)) {
      hans += 1;
    } else if (HANT_ONLY.has(ch)) {
      hant += 1;
    }
  }
  if (hans === hant) {
    return undefined;
  }
  return hant > hans ? 'zh-Hant' : 'zh-Hans';
}
